import React from 'react';
import { useLanguage } from '../App';

const Mushroom = () => {
  const { t } = useLanguage();
  const m = t.mushroom;

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero Banner - Upcoming Project */}
      <section className="bg-green-800 text-white py-16 px-4">
        <div className="container-custom text-center">
          <span className="inline-block bg-yellow-400 text-green-900 text-sm font-bold px-4 py-1 rounded-full mb-4 uppercase tracking-wide">
            {m.badge}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{m.heroTitle}</h1>
          <p className="text-green-200 text-lg max-w-2xl mx-auto mb-2">{m.heroSubtitle}</p>
          <p className="text-green-300 text-sm">{m.location}</p>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-12 px-4 bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-green-800 mb-3">{m.overviewTitle}</h2>
          <p className="text-gray-700 leading-relaxed mb-4">{m.overviewDesc}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              { label: m.stat1Label, value: m.stat1Value },
              { label: m.stat2Label, value: m.stat2Value },
              { label: m.stat3Label, value: m.stat3Value },
              { label: m.stat4Label, value: m.stat4Value },
            ].map((s, i) => (
              <div key={i} className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                <p className="text-2xl font-bold text-green-700">{s.value}</p>
                <p className="text-sm text-gray-600 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-green-800 mb-2">{m.productsTitle}</h2>
          <p className="text-gray-600 mb-6">{m.productsSubtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: '🍄', title: m.prod1Title, desc: m.prod1Desc, price: m.prod1Price },
              { icon: '🌿', title: m.prod2Title, desc: m.prod2Desc, price: m.prod2Price },
              { icon: '🧂', title: m.prod3Title, desc: m.prod3Desc, price: m.prod3Price },
            ].map((p, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="text-4xl mb-3">{p.icon}</div>
                <h3 className="font-bold text-green-800 text-lg mb-2">{p.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{p.desc}</p>
                <p className="text-green-700 font-semibold text-sm">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Oyster Mushroom */}
      <section className="py-12 px-4 bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-green-800 mb-6">{m.whyTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              m.why1, m.why2, m.why3, m.why4, m.why5, m.why6
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                <span className="text-green-600 mt-1 text-lg">✔</span>
                <p className="text-gray-700 text-sm">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-green-800 mb-2">{m.infraTitle}</h2>
          <p className="text-gray-600 mb-6">{m.infraSubtitle}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              m.infra1, m.infra2, m.infra3, m.infra4, m.infra5, m.infra6
            ].map((item, i) => (
              <div key={i} className="bg-white border border-green-100 rounded-lg p-4 text-center">
                <p className="text-gray-700 text-sm font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Highlights */}
      <section className="py-12 px-4 bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-green-800 mb-6">{m.finTitle}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
              <thead className="bg-green-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">{m.finCol1}</th>
                  <th className="px-4 py-3 text-left">{m.finCol2}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [m.fin1L, m.fin1V],
                  [m.fin2L, m.fin2V],
                  [m.fin3L, m.fin3V],
                  [m.fin4L, m.fin4V],
                  [m.fin5L, m.fin5V],
                  [m.fin6L, m.fin6V],
                ].map(([label, val], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-3 text-gray-700 font-medium">{label}</td>
                    <td className="px-4 py-3 text-green-700 font-semibold">{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 px-4 bg-green-50">
        <div className="container-custom max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-green-800 mb-2">{m.timelineTitle}</h2>
          <p className="text-gray-600 mb-8">{m.timelineSubtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { phase: m.phase1, desc: m.phase1Desc, months: m.phase1Months },
              { phase: m.phase2, desc: m.phase2Desc, months: m.phase2Months },
              { phase: m.phase3, desc: m.phase3Desc, months: m.phase3Months },
            ].map((ph, i) => (
              <div key={i} className="bg-white rounded-xl p-5 shadow-sm border-t-4 border-green-600">
                <p className="text-xs text-green-600 font-bold uppercase mb-1">{ph.months}</p>
                <h3 className="font-bold text-green-800 mb-2">{ph.phase}</h3>
                <p className="text-gray-600 text-sm">{ph.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Contact for Inquiry */}
      <section className="py-12 px-4 bg-green-800 text-white text-center">
        <div className="container-custom max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-3">{m.ctaTitle}</h2>
          <p className="text-green-200 mb-6">{m.ctaDesc}</p>
          <a
            href="/contact"
            className="inline-block bg-yellow-400 text-green-900 font-bold px-8 py-3 rounded-lg hover:bg-yellow-300 transition-colors"
          >
            {m.ctaBtn}
          </a>
        </div>
      </section>

    </div>
  );
};

export default Mushroom;
